import { useMemo, useState } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { simulationApi } from '@/events/simulationEngine';
import { addDays, format, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { customerName, simTimeFull } from '@/utils/format';
import { CalendarDays, Send, CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';

export default function FutureReadinessCalendar() {
  const signals = useBrainStore((s) => s.futureSignals);
  const customers = useBrainStore((s) => s.customers);
  const upsertFutureSignal = useBrainStore((s) => s.upsertFutureSignal);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const addConversationEntry = useBrainStore((s) => s.addConversationEntry);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const [pickedIso, setPickedIso] = useState<string | null>(null);
  const [justSent, setJustSent] = useState<Set<string>>(new Set());

  const approveAndSend = (sigId: string) => {
    const sig = signals[sigId];
    if (!sig) return;
    const cust = customers[sig.customerId];
    upsertFutureSignal({ ...sig, status: 'fired' });
    if (cust && sig.draftMessage) {
      addConversationEntry(sig.customerId, {
        id: `convo-fr-${sigId}-${Date.now()}`,
        customerId: sig.customerId,
        channel: sig.recommendedChannel,
        direction: 'out',
        body: sig.draftMessage,
        timestamp: clockIso,
        authorRole: 'agent',
        authorName: 'You',
      });
    }
    appendActivity({
      id: `act-fr-${sigId}-${Date.now()}`,
      type: 'future-readiness-fired',
      customerId: sig.customerId,
      body: `Future-readiness sent on ${sig.recommendedChannel} to ${cust ? customerName(cust) : sig.customerId}.`,
      timestamp: clockIso,
    });
    pushNotification({
      intensity: 'high',
      title: 'Sent',
      body: `${cust ? customerName(cust) : 'Customer'} pinged on ${sig.recommendedChannel}.`,
      ttlMs: 4000,
    });
    setJustSent((prev) => new Set(prev).add(sigId));
  };

  const monthStart = useMemo(() => startOfMonth(new Date('2026-05-01')), []);
  const monthEnd = useMemo(() => endOfMonth(addDays(monthStart, 60)), [monthStart]);
  const days = useMemo(() => eachDayOfInterval({ start: monthStart, end: monthEnd }), [monthStart, monthEnd]);

  const signalsByDay = useMemo(() => {
    const map: Record<string, typeof signals[string][]> = {};
    for (const s of Object.values(signals)) {
      const k = format(new Date(s.scheduledFor), 'yyyy-MM-dd');
      if (!map[k]) map[k] = [];
      map[k].push(s);
    }
    return map;
  }, [signals]);

  const pickedSignals = pickedIso
    ? (signalsByDay[format(new Date(pickedIso), 'yyyy-MM-dd')] ?? [])
    : Object.values(signals);

  return (
    <section className="px-8 py-8" data-testid="future-readiness-calendar">
      <header className="flex items-end justify-between max-w-6xl">
        <div>
          <p className="label-small-caps text-gold-bright">PRD §12.17</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Future-Readiness Calendar</h1>
          <p className="font-serif text-cream-soft mt-3">
            Every May-would-be-good signal becomes a calendar event. Tap a day to see scheduled
            re-contacts.
          </p>
        </div>
        <button
          type="button"
          onClick={() => simulationApi.triggerEvent('daniel-future-readiness-fires')}
          className="btn-ghost text-xs"
          data-testid="fire-daniel-may"
        >
          <Send size={12} /> Simulate May trigger
        </button>
      </header>

      <div className="mt-6 grid grid-cols-12 gap-6 max-w-6xl">
        <section className="col-span-8 panel p-4">
          <h3 className="label-small-caps mb-3 text-gold-bright">
            <CalendarDays size={12} className="inline mr-1" />
            Density heatmap
          </h3>
          <div className="grid grid-cols-7 gap-1 text-[10px]">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <span key={d} className="text-cream-soft font-inter text-center uppercase tracking-widest">
                {d}
              </span>
            ))}
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const count = signalsByDay[key]?.length ?? 0;
              const isToday = isSameDay(day, new Date(clockIso));
              const intensity = Math.min(1, count / 3);
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => setPickedIso(day.toISOString())}
                  className={clsx(
                    'aspect-square rounded-sm flex flex-col items-center justify-center font-mono text-xs',
                    isToday ? 'border-2 border-gold-bright' : 'border border-gold-dim/15',
                  )}
                  style={{
                    backgroundColor:
                      count === 0
                        ? 'rgb(var(--surface-3) / 0.35)'
                        : `rgb(var(--accent-gold-bright) / ${0.18 + intensity * 0.5})`,
                    color:
                      count > 0
                        ? 'rgb(var(--text-strong))'
                        : 'rgb(var(--text-faint))',
                  }}
                  data-testid={`heatmap-day-${key}`}
                >
                  <span>{format(day, 'd')}</span>
                  {count > 0 ? <span className="text-[9px]">{count}</span> : null}
                </button>
              );
            })}
          </div>
        </section>

        <aside className="col-span-4">
          <h3 className="label-small-caps text-gold-bright mb-3">
            {pickedIso ? format(new Date(pickedIso), 'EEEE d MMMM') : 'All scheduled re-contacts'}
          </h3>
          <ol className="space-y-2">
            {pickedSignals.length === 0 ? (
              <li className="font-serif text-cream-soft text-sm">
                Nothing scheduled here yet.
              </li>
            ) : null}
            {pickedSignals.slice(0, 8).map((sig) => {
              const cust = customers[sig.customerId];
              const sent = justSent.has(sig.id) || sig.status === 'fired';
              return (
                <li key={sig.id} className="panel-dark p-3" data-testid={`signal-${sig.id}`}>
                  <p className="font-inter text-sm text-cream">
                    {cust ? customerName(cust) : sig.customerId}
                  </p>
                  <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                    {simTimeFull(sig.scheduledFor)} · via {sig.recommendedChannel}
                  </p>
                  <p className="font-serif text-xs text-cream-soft mt-2">
                    &ldquo;{sig.rawPhrase}&rdquo;
                  </p>
                  {sig.draftMessage ? (
                    <p className="mt-3 text-xs text-cream bg-gold/5 p-2 rounded border border-gold-dim/30">
                      Draft: {sig.draftMessage}
                    </p>
                  ) : null}
                  {sent ? (
                    <p
                      className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-mint/15 text-mint text-[11px] font-inter uppercase tracking-widest"
                      data-testid={`signal-sent-${sig.id}`}
                    >
                      <CheckCircle2 size={10} /> Sent on {sig.recommendedChannel}
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => approveAndSend(sig.id)}
                      className="btn-primary text-[10px] mt-3"
                      data-testid={`signal-approve-${sig.id}`}
                    >
                      <Send size={10} /> Approve and send
                    </button>
                  )}
                </li>
              );
            })}
          </ol>
        </aside>
      </div>
    </section>
  );
}
